import qs from 'qs'
import { autocompleteCity, reverseGeocode } from '@lib/geo-api'
import algoliasearch from 'algoliasearch'
import { isEmpty, path } from 'ramda'
import { DateTime } from 'luxon'

export const searchClient = algoliasearch(
  process.env.ALGOLIA_APP_ID,
  process.env.ALGOLIA_API_KEY,
)

const index = searchClient.initIndex(process.env.ALGOLIA_GROUP_EVENTS_INDEX_NAME)

export const updateAfterMilliseconds = 700

export const createURL = state => `?${qs.stringify(state)}`

const plusToSpace = str => str.replace('+', ' ')

const getPlacesFromUrl = async (params, aroundLatLng) => {
  const placeQuery = params.length ? params.reduce((acc, cv) => { return `${acc} ${plusToSpace(cv)}` }, '') : null
  if (placeQuery) {
    const [city] = await autocompleteCity(placeQuery)
    if (!city) return []
    return [
      {
        cityName: city.city,
        stateName: city.state,
        country: 'US',
        _geoloc: city._geoloc,
      },
    ]
  }
  if (aroundLatLng) {
    const city = await reverseGeocode(aroundLatLng)
    if (!city) return []
    return [{
      cityName: city.city,
      stateName: city.state,
      country: 'US',
      _geoloc: city._geoloc,
    }]
  }
  return []
}

export const pathToSearchState = async (urlPath, params = []) => {
  const queryParams = urlPath.includes('?') ? qs.parse(urlPath.substring(urlPath.indexOf('?') + 1)) : {}
  const placesRes = await getPlacesFromUrl(params, queryParams.aroundLatLng)
  return {
    ...(!isEmpty(queryParams) && { ...queryParams }),
    ...(placesRes.length && { aroundLatLng: `${placesRes[0]._geoloc.lat}, ${placesRes[0]._geoloc.lng}` }),
    ...(placesRes.length && { placeName: `${placesRes[0].cityName}, ${placesRes[0].stateName}` }),
  }
}

export const searchStateToURL = async (searchState, aroundLatLng) => {
  const searchStateWithCoords = {
    ...searchState,
    ...(aroundLatLng && { aroundLatLng }),
  }
  return `${window.location.pathname}?${qs.stringify(searchStateWithCoords)}`
}

export const DEFAULT_PROPS = {
  searchClient,
  indexName: process.env.ALGOLIA_GROUP_EVENTS_INDEX_NAME,
}

const flattenArrToStr = (arr = []) => {
  if (!arr.length) return ''
  return arr.reduce((acc, cv) => {
    if (!isEmpty(acc)) {
      return `${acc}, ${cv}`
    }
    return cv
  }, '')
}

const getQueryAndLocation = (query, placeName) => {
  if (query && placeName) {
    return `${query} in ${placeName}`
  }
  if (query) return query
  if (placeName) return placeName
  return 'No Query Entered'
}

export const makeGaFriendlyObj = (searchState) => {
  const { query, placeSearchValue, placeName } = searchState

  const price = path(['multiRange', 'total_price_in_cents'], searchState)
  const priceArr = price ? [`$${price.slice(1) / 100}`] : []
  const tags = path(['refinementList', 'tags'], searchState)
  const tagsArr = tags && tags.length > 0 ? tags : []
  const date = path(['range', 'start_date_time_ts', 'min'], searchState)
  const dateArr = date ? [DateTime.fromSeconds(Number(date)).toFormat('yyyy-LL-dd')] : []
  const registrationSize = path(['toggle', 'registration_full'], searchState) ? 'registration_full_true' : null
  const regArr = registrationSize ? [registrationSize] : []
  const refinersArr = tagsArr.concat(priceArr).concat(dateArr).concat(regArr)
  const eventSearchRefiners = flattenArrToStr(refinersArr)
  return {
    eventSearchQuery: getQueryAndLocation(query, placeSearchValue || placeName),
    ...(!isEmpty(eventSearchRefiners) && { eventSearchRefiners }),
  }
}

export const getAllIndex = ({
  aroundLatLng, aroundRadius, tags, priceInCents, totalPriceInCents, startDateTime, registrationFull,
} = {}) => {
  const currentTime = new Date().getTime() / 1000
  const response = new Promise((resolve) => {
    // Array containing predicates for query, will be joined by AND
    const queryPieces = []

    // Start query generation, ensure registration is open
    queryPieces.push('registration_closed:false')

    // filter out canceled events
    queryPieces.push('is_canceled:false')

    // Add tags to query
    if (tags.length > 0) {
      let tagsQueryPieces = []
      let tagsQuery = ''
      if (typeof tags === 'string') {
        tagsQuery = `tags:'${tags}'`
      } else {
        tagsQueryPieces = tags.map(tag => `tags:'${tag}'`)
        tagsQuery = tagsQueryPieces.join(' OR ')
      }
      queryPieces.push(tagsQuery)
    }

    // Add price range to query
    if (priceInCents) {
      const [fromPrice, toPrice] = priceInCents.split(':') || [0, 0]
      queryPieces.push(`price_in_cents >= ${fromPrice || 0} AND price_in_cents <= ${toPrice}`)
    }

    // Add total price range to query
    if (totalPriceInCents) {
      const [fromTotalPrice, toTotalPrice] = totalPriceInCents.split(':') || [0, 0]
      queryPieces.push(`total_price_in_cents >= ${fromTotalPrice || 0} AND total_price_in_cents <= ${toTotalPrice}`)
    }

    // Add start date time range to query
    if (startDateTime) {
      queryPieces.push(`start_date_time_ts > ${startDateTime.min} AND start_date_time_ts <= ${startDateTime.max} AND registration_close_date_time_ts > ${currentTime}`)
    } else {
      queryPieces.push(`start_date_time_ts > ${currentTime} AND registration_close_date_time_ts > ${currentTime}`)
    }

    // Add registration full toggle to query
    if (registrationFull) {
      queryPieces.push('registration_full:false')
    }

    // Join query pieces into predicates wrapped in parentheses and join with AND
    const query = queryPieces.map(q => `(${q})`).join(' AND ')

    index.search({
      filters: query,
      hitsPerPage: 10000,
      aroundLatLng,
      aroundRadius,
      aroundLatLngViaIP: !aroundLatLng,
    }).then(({ hits }) => {
      resolve(hits)
    })
  })
  return response
}
